import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { ONE_DAY_IN_MS } from "@/v2-backoffice-client/constants"
import {
  activityLevel,
  localIsoDate,
  type UsageActivityLevel,
} from "@/v2-backoffice-client/usage/usage.utils"

export type UsageDayStripProps = ComponentProps<"div"> & {
  /** One value per day, oldest first (weighted daily totals). */
  series: number[]
  /** Tooltip noun for the values ("5 active users"); default "activity". */
  unit?: string
}

// Green intensity ramp — the same activity-calendar language as the
// panel's history strip and week grid (usage.css), distinct from the amber
// UI chrome.
const DAY_STRIP_LEVEL_CLASSES: Record<UsageActivityLevel, string> = {
  empty: "bg-muted",
  low: "bg-green-500/30",
  mid: "bg-green-500/60",
  high: "bg-green-500",
}

// 28-block activity strip (uptime-history-bar style): one cell per day,
// intensity by share of the user's own busiest day. Native `title` tooltips
// instead of Radix so hundreds of rows stay cheap.
function UsageDayStrip(props: UsageDayStripProps) {
  const { series, unit = "activity", className, ...divProps } = props

  const max = Math.max(1, ...series)
  const today = new Date()

  return (
    <div
      className={cn("flex h-4 items-stretch gap-px", className)}
      role="img"
      aria-label={`${series.length}-day activity history`}
      {...divProps}
    >
      {series.map((value, i) => {
        const daysAgo = series.length - 1 - i
        const date = new Date(today.getTime() - daysAgo * ONE_DAY_IN_MS)
        const label = `${localIsoDate(date)}: ${
          value > 0 ? `${value.toFixed(0)} ${unit}` : `no ${unit}`
        }`
        return (
          <div
            key={i}
            title={label}
            className={cn(
              "min-w-0 flex-1 rounded-[1px]",
              DAY_STRIP_LEVEL_CLASSES[activityLevel(value, max)],
            )}
          />
        )
      })}
    </div>
  )
}

export default UsageDayStrip
